import { type ReactNode, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx'

import { UserAvatar } from '@/components/UserAvatar.tsx'
import { SearchSelectCommand } from '@/components/ui/search-select-command.tsx'

type MemberSelectorProps = {
  currentMember?: Member
  members: Member[]
  selectedMembers: Member[]
  onSelect: (item: Member) => void
  onRemove?: (item: Member) => void
  children: ReactNode
}

export const MemberSelector = ({
  currentMember,
  members,
  selectedMembers,
  onRemove,
  onSelect,
  children,
}: MemberSelectorProps) => {
  const [open, setOpen] = useState(false)

  return (
    <li className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent sideOffset={8} align="end" alignOffset={-8} className="p-0 w-40 rounded-xs border-none">
          <div aria-label="member-dropdown" className="shadow-dropshadow-300 rounded-xs overflow-hidden">
            {currentMember && (
              <div className="py-1 pl-2 pr-1.5 bg-surface-secondary">
                <div className="flex items-center justify-between h-6">
                  <span className="text-contents-onDarkPrimary font-EN-KR-caption-bold">{currentMember.name}</span>
                  {onRemove && (
                    <Button
                      variant="primary"
                      type="button"
                      className="w-13.5 h-5 px-1 rounded-xs font-EN-KR-caption-bold"
                      onClick={() => onRemove(currentMember)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            )}
            <SearchSelectCommand
              items={members.filter(
                (member) => !selectedMembers.some((selectedMember) => selectedMember.id === member.id),
              )}
              selectedItems={selectedMembers}
              SearchResultItemComponent={MemberSearchResultItem}
              onSelect={(item) => {
                onSelect(item)
                setOpen(false)
              }}
              placeholder="Search a member"
              emptyMessage="No member found."
            />
          </div>
        </PopoverContent>
      </Popover>
    </li>
  )
}

type MemberSearchResultItemProps = {
  item: Member
}

const MemberSearchResultItem = ({ item }: MemberSearchResultItemProps) => {
  return (
    <div className="flex gap-1 items-center justify-start w-full py-1.5 px-2 relative hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8 after:absolute after:inset-0 font-EN-KR-caption-regular text-contents-primary">
      <UserAvatar member={item} size="xs" />
      <span>{item.name}</span>
    </div>
  )
}
